import React, { useEffect } from 'react'
import styles from './ThanksPage.module.css'
import { ThanksPageProps } from './types'
import { useAbTestGtm } from '@/components/utils/abTest'
import { ScoutOffer } from './ScoutOffer'
import { LineLinkModal } from './LineLinkModal'
import { useLineLinkModalTrigger } from './LineLinkModal/useLineLinkModalTrigger'

// AGLPサンクスページ経由のPFCV計測用マーケティングコード
const SCOUT_MC_CODE = '16700000000015028300000'

const getPfOrigin = () => {
    const pfDomain = window.ENV?.PF_DOMAIN || window.location.host
    const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:'
    return `${protocol}//${pfDomain}`
}

const ThanksPage: React.FC<ThanksPageProps> = ({
    jobSeeker,
    location = 'kanto',
}) => {
    // propsから直に導出する。stateへ写すと初回レンダーだけ未連携として描かれ、KUZEN連携済みの
    // 人にもページ内のLINE連携バナーとその data-view-label が一瞬DOMに載る（計測が汚れる）
    const isKuzenLinked = !!jobSeeker?.kuzen_linked
    const encodedEmail = jobSeeker?.encoded_email || ''

    const isRegistered = jobSeeker?.id && jobSeeker?.registered
    const hasJobSeeker = jobSeeker?.id

    const getImagePath = (filename: string) => {
        return `/pages/thanks/${filename}`
    }

    // onlineの場合はkantoの画像を使用
    const getLocationForImage = (loc: string) => {
        return loc === 'online' ? 'kanto' : loc
    }

    const getKuzenLinkUrl = () => {
        if (!encodedEmail) return '#'
        return `https://liff.line.me/2007824586-AmeOdXEP?sl=16a5613000&encoded_email=${encodedEmail}&path=lp_ag_thanks`
    }

    const { dataViewLabel, dataClickLabel } = useAbTestGtm()
    // スカウト（PF転換）訴求は「会員登録済みだがPF未転換」の人にだけ出す。
    // 未登録・既にPF転換済みの人には従来のレイアウト（KUZEN未連携ならページ内のLINE連携バナー、
    // 連携済みならTEL画像）を出す
    const isScoutOfferTarget = !!hasJobSeeker && !isRegistered

    const getUpgradeUrl = () => {
        if (!jobSeeker?.register_token) return '#'
        return `${getPfOrigin()}/job_seekers/${jobSeeker.register_token}/edit?mc=${SCOUT_MC_CODE}`
    }
    // KUZEN連携済みの人にはページ内のLINE連携バナーも出していないため、モーダルも出さない
    const isLineLinkModalEnabled =
        isScoutOfferTarget && !isKuzenLinked && !!encodedEmail
    const lineLinkModal = useLineLinkModalTrigger(isLineLinkModalEnabled)

    const telImageSrcSp = getImagePath(
        `entry_thanks_tel_${getLocationForImage(location)}${isKuzenLinked ? '' : '_no_id'}_sp.png`,
    )

    const thanksPageGtmId =
        (typeof window !== 'undefined' && window.ENV?.GTM_ID) || 'GTM-5K6833JW'

    useEffect(() => {
        const gtmId = window.ENV?.GTM_ID || 'GTM-5K6833JW'
        const w = window as Window & { dataLayer?: unknown[] }
        w.dataLayer = w.dataLayer || []
        w.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
        const script = document.createElement('script')
        script.async = true
        script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`
        document.head.appendChild(script)
    }, [])

    const scoutOfferProps = {
        telImageSrc: telImageSrcSp,
        upgradeUrl: getUpgradeUrl(),
    }

    return (
        <div className={styles.thanksBody}>
            <noscript>
                <iframe
                    src={`https://www.googletagmanager.com/ns.html?id=${thanksPageGtmId}`}
                    height={0}
                    width={0}
                    style={{ display: 'none', visibility: 'hidden' }}
                    title="Google Tag Manager"
                />
            </noscript>
            <header
                data-view-label={dataViewLabel('Thanks_HD_離脱POP')}
                className={styles.thanksHeader}>
                <img src='/pages/logo/logo.png' alt='foodsLabo' />
            </header>

            <div className={styles.thanksWrap}>
                {isScoutOfferTarget ? (
                    <ScoutOffer {...scoutOfferProps} />
                ) : (
                    <section className={styles.thanksWrapSection}>
                        <div style={{ padding: '0 20px' }}>
                            <img
                                src={getImagePath('entry_thanks_text.png')}
                                alt=''
                                className={styles.entryThanksText}
                            />
                        </div>

                        {isKuzenLinked ? (
                            <>
                                <div className={styles.thanksContent}>
                                    <img
                                        src={getImagePath(
                                            `entry_thanks_tel_${getLocationForImage(location)}_pc.png`,
                                        )}
                                        alt=''
                                        className={styles.entryThanksTelPc}
                                    />
                                </div>
                                <div className={styles.thanksContent}>
                                    <img
                                        src={getImagePath(
                                            `entry_thanks_tel_${getLocationForImage(location)}_sp.png`,
                                        )}
                                        alt=''
                                        className={styles.entryThanksTelSp}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <div data-view-label={dataViewLabel('Thanks_CT_LINE-連携')}>
                                    <a
                                        href={getKuzenLinkUrl()}
                                        data-click-label={dataClickLabel('Thanks_CT_LINE-連携_CTAボタン')}
                                    >
                                        <div className={styles.thanksContent}>
                                            <img
                                                src={getImagePath(
                                                    'kuzen-foodslabo-thanks-banner-pc.png',
                                                )}
                                                alt='KUZEN ID連携バナー'
                                                className={styles.entryThanksTelPc}
                                            />
                                        </div>
                                        <div className={styles.thanksContent}>
                                            <img
                                                src={getImagePath(
                                                    'kuzen-foodslabo-thanks-banner-sp.png',
                                                )}
                                                alt='KUZEN ID連携バナー'
                                                className={styles.entryThanksTelSp}
                                            />
                                        </div>
                                    </a>
                                </div>
                                <div className={styles.thanksContent}>
                                    <img
                                        src={getImagePath(
                                            `entry_thanks_tel_${getLocationForImage(location)}_no_id_pc.png`,
                                        )}
                                        alt='TEL画像'
                                        className={styles.entryThanksTelPc}
                                    />
                                </div>
                                <div className={styles.thanksContent}>
                                    <img
                                        src={getImagePath(
                                            `entry_thanks_tel_${getLocationForImage(location)}_no_id_sp.png`,
                                        )}
                                        alt='TEL画像'
                                        className={`${styles.entryThanksTelSp} ${styles.entryThanksTelNoIdSp}`}
                                    />
                                </div>
                            </>
                        )}

                        <div
                            className={
                                isRegistered
                                    ? styles.entryThanksBottomContents
                                    : styles.entryThanksBottomContentsDefault
                            }
                        >
                            <img
                                src={getImagePath(
                                    'entry_thanks_bottom_contents.png',
                                )}
                                alt=''
                            />
                        </div>
                    </section>
                )}

                <footer className={styles.thanksFooter}>
                    <small>
                        厚生労働大臣許可番号有料職業紹介事業許可番号13-ユ-308506
                        <br />
                        Copyright &copy; {new Date().getFullYear()} foodsLabo by
                        cuolega All rights reserved.
                    </small>
                    <div>
                        <a href='https://cuolega.co.jp/privacy/'>
                            プライバシーポリシー
                        </a>
                        <a href='https://cuolega.co.jp/terms/'>利用規約</a>
                        <a href='https://cuolega.co.jp/profile/'>会社概要</a>
                    </div>
                </footer>
            </div>

            {lineLinkModal.isOpen && (
                <LineLinkModal lineUrl={getKuzenLinkUrl()} onClose={lineLinkModal.close} />
            )}
        </div>
    )
}

export default ThanksPage
